/**
 * Instructions Screen Component
 * Displays mode-specific instructions before gameplay
 */

import { GameMode } from '../core/GameEngine';

interface InstructionsScreenProps {
  mode: GameMode;
  onStartGame: () => void;
  onBack: () => void;
}

interface ModeInstructions {
  title: string;
  emoji: string;
  color: string;
  description: string;
  instructions: string[];
  tips: string[];
}

const modeInstructions: Record<GameMode, ModeInstructions> = {
  classic: {
    title: 'Classic Mode',
    emoji: '🎮',
    color: 'var(--accent-g)',
    description: 'Pure Simon Says - the original memory challenge',
    instructions: [
      'Watch the sequence of colored buttons light up',
      'Repeat the exact sequence by tapping the buttons',
      'Each round adds one more button to the sequence',
      'One mistake ends the game',
      'Score increases with each correct round'
    ],
    tips: [
      'Focus on the pattern, not just individual colors',
      'Take your time - there\'s no rush between rounds'
    ]
  },
  chaos: {
    title: 'Chaos Mode',
    emoji: '🌀',
    color: 'var(--accent-r)',
    description: 'Moving buttons, decoys, and invisible challenges',
    instructions: [
      'Buttons move around after each round',
      'Decoy "intruder" buttons appear - don\'t tap them!',
      'Some rounds make buttons invisible - tap carefully',
      'Real buttons glow briefly when revealed',
      'Sequence difficulty increases with your score'
    ],
    tips: [
      'Track button positions as they move',
      'In invisible mode, wait for the reveal glow',
      'Intruders never light up in the sequence'
    ]
  },
  rhythm: {
    title: 'Rhythm Mode',
    emoji: '💃',
    color: 'var(--accent-y)',
    description: 'Match the beat with precision timing',
    instructions: [
      'Timing matters! Tap buttons at the right moment',
      'Watch for visual feedback: PERFECT, GOOD, LATE, or MISS',
      'Perfect timing builds your panic multiplier',
      'Panic mode activates at 3x - buttons speed up!',
      'Miss too many and your multiplier resets'
    ],
    tips: [
      'Listen to the rhythm and feel the beat',
      'Aim for PERFECT taps to maximize your score',
      'Stay calm during panic mode - speed matters'
    ]
  },
  puzzle: {
    title: 'Puzzle Mode',
    emoji: '🧩',
    color: 'var(--accent-b)',
    description: 'Advanced challenges for master players',
    instructions: [
      'Complex patterns and sequences',
      'Multiple simultaneous challenges',
      'Advanced memory techniques required',
      'Unlock special achievements',
      'Master all other modes first'
    ],
    tips: [
      'Practice other modes before attempting',
      'Focus on pattern recognition',
      'Take breaks if needed'
    ]
  }
};


export function InstructionsScreen({ mode, onStartGame, onBack }: InstructionsScreenProps) {
  const info = modeInstructions[mode];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      minHeight: '100vh',
      width: '100%',
      padding: 'max(20px, env(safe-area-inset-top)) 20px max(20px, env(safe-area-inset-bottom))',
      background: 'var(--bg)',
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        marginTop: '20px'
      }}>
        <div style={{
          fontSize: '64px',
          lineHeight: 1
        }}>
          {info.emoji}
        </div>
        <h1 style={{
          fontFamily: 'Syne, sans-serif',
          fontSize: 'clamp(28px, 6vw, 40px)',
          fontWeight: 800,
          letterSpacing: '-0.5px',
          color: info.color,
          margin: 0,
          textAlign: 'center'
        }}>
          {info.title}
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'var(--muted)',
          margin: 0,
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          {info.description}
        </p>
      </div>

      {/* Instructions */}
      <div style={{
        width: '100%',
        maxWidth: '500px',
        marginBottom: '24px'
      }}>
        <h2 style={{
          fontFamily: 'Syne, sans-serif',
          fontSize: '18px',
          fontWeight: 700,
          color: 'var(--text)',
          marginBottom: '12px'
        }}>
          How to Play
        </h2>
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {info.instructions.map((instruction, index) => (
            <li key={index} style={{
              display: 'flex',
              gap: '12px',
              fontSize: '15px',
              lineHeight: '1.5',
              color: 'var(--text)'
            }}>
              <span style={{
                color: info.color,
                fontWeight: 700,
                flexShrink: 0
              }}>
                {index + 1}.
              </span>
              <span>{instruction}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Tips */}
      <div style={{
        width: '100%',
        maxWidth: '500px',
        marginBottom: '32px'
      }}>
        <h2 style={{
          fontFamily: 'Syne, sans-serif',
          fontSize: '18px',
          fontWeight: 700,
          color: 'var(--text)',
          marginBottom: '12px'
        }}>
          💡 Tips
        </h2>
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {info.tips.map((tip, index) => (
            <li key={index} style={{
              display: 'flex',
              gap: '8px',
              fontSize: '14px',
              lineHeight: '1.5',
              color: 'var(--muted)',
              paddingLeft: '12px',
              borderLeft: `3px solid ${info.color}40`
            }}>
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
        maxWidth: '500px',
        marginTop: 'auto',
        paddingTop: '20px'
      }}>
        {/* Play Button */}
        <button
          onClick={onStartGame}
          style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: '18px',
            fontWeight: 700,
            padding: '16px 32px',
            background: info.color,
            color: '#000',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 200ms ease',
            outline: 'none',
            width: '100%'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = `0 8px 24px ${info.color}60`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          ▶️ Start Game
        </button>

        {/* Back Button */}
        <button
          onClick={onBack}
          style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: '16px',
            fontWeight: 600,
            padding: '12px 24px',
            background: 'transparent',
            color: 'var(--muted)',
            border: '2px solid var(--border)',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 200ms ease',
            outline: 'none',
            width: '100%'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--text)';
            e.currentTarget.style.borderColor = 'var(--text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--muted)';
            e.currentTarget.style.borderColor = 'var(--border)';
          }}
        >
          ← Back to Mode Selection
        </button>
      </div>
    </div>
  );
}
